import { useMemo } from 'react'

import type { AiConfigApiClient } from '../api/client'
import { useListAgents } from '../queries/aiConfigQueries'

/**
 * Fetches AI agents for a PBX that can be used in the AI Agent switchboard node.
 *
 * Only agents without a signalsAiPhoneNumber are returned — agents with a phone
 * number are used by the signals AI path and are not valid switchboard targets.
 *
 * @param api - AiConfigApiClient from createAiConfigApi()
 * @param pbxId - The PBX ID to fetch agents for, or null to skip fetching
 */
export const useGetAiAgents = (
  api: AiConfigApiClient,
  pbxId: number | null,
) => {
  const { agents, isLoading } = useListAgents(
    api,
    pbxId ? { 'mango-pbx-id': pbxId, limit: 100 } : {},
    { enabled: !!pbxId },
  )

  const aiAgents = useMemo(() => {
    if (!pbxId) return []
    return agents
      .filter((agent) => !agent.signalsAiPhoneNumber)
      .map((agent) => ({
        id: agent.id,
        name: agent.agentName,
        title: agent.title,
      }))
  }, [agents, pbxId])

  return { aiAgents, isLoading }
}
